import type { CreateRequestBodyTypeEnumKey, RequestListItemStatusEnumKey } from "@synthesis/shared-services/external/application/types";
import type { InternalRequestDetailResponseStatusEnumKey } from "@synthesis/shared-services/internal/application/types";

export type ApplicationSelectOption = {
  value: string;
  label: string;
};

export type ApplicationStatus = RequestListItemStatusEnumKey | InternalRequestDetailResponseStatusEnumKey;

export const APPLICATION_TYPE_FALLBACK_LABEL: Record<CreateRequestBodyTypeEnumKey, string> = {
  TU_ISSUE: "Выпуск ТУ",
  TU_CHANGE: "Изменение ТУ",
  APPROVAL: "Согласование",
};

export const EXTERNAL_APPLICATION_STATUS_FALLBACK_LABEL: Record<RequestListItemStatusEnumKey, string> = {
  DRAFT: "Черновик",
  IN_REVIEW: "Направлено в РЖД",
  REVISION_REQUIRED: "Требуется доработка",
  WAITING_PAYMENT: "Ожидает оплаты",
  RESOLVED_APPROVED: "ТУ получено",
  RESOLVED_REJECTED: "Мотивированный отказ",
};

export const INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL: Record<InternalRequestDetailResponseStatusEnumKey, string> = {
  INTAKE_CHECK: "Входной контроль",
  REVISION_REQUIRED: "На доработке",
  SENT_TO_NG: "Направлено в аппарат НГ",
  IN_NG: "В работе у аппарата НГ",
  SENT_TO_NTP: "Направлено в НТП",
  IN_NTP: "В работе у НТП",
  NTP_RESP_ASSIGNED: "Назначен ответственный НТП",
  COMMISSION_PREP: "Подготовка комиссии",
  DECISION_FORMING: "Формируется решение",
  NTP_APPROVAL: "Согласование НТП",
  NG_APPROVAL: "Согласование НГ",
  DECISION_REWORK: "Решение на доработке",
  TU_ISSUED: "Выдано ТУ",
  REJECTED: "Мотивированный отказ",
};

export const EDITABLE_EXTERNAL_APPLICATION_STATUSES = ["DRAFT", "REVISION_REQUIRED"] as const satisfies readonly RequestListItemStatusEnumKey[];

export const isEditableExternalApplicationStatus = (status?: ApplicationStatus | null): boolean =>
  status != null && (EDITABLE_EXTERNAL_APPLICATION_STATUSES as readonly string[]).includes(status);

export const APPLICATION_STATUS_FALLBACK_LABEL: Record<ApplicationStatus, string> = {
  ...EXTERNAL_APPLICATION_STATUS_FALLBACK_LABEL,
  ...INTERNAL_APPLICATION_STATUS_FALLBACK_LABEL,
};

export const RAILWAY_OPTIONS: ApplicationSelectOption[] = [
  { value: "east-siberian", label: "Восточно-Сибирская железная дорога" },
  { value: "gorky", label: "Горьковская железная дорога" },
  { value: "far-eastern", label: "Дальневосточная железная дорога" },
  { value: "zabaikalsky", label: "Забайкальская железная дорога" },
];

export const APPEAL_CASE_OPTIONS: ApplicationSelectOption[] = [
  { value: "new-adjacent", label: "Новое примыкание" },
  { value: "existing-adjacent-development", label: "Развитие существующего примыкания" },
  { value: "cargo-flow-increase", label: "Увеличение грузопотока" },
  { value: "other-case", label: "Иной случай" },
];

export const CROSSING_METHOD_OPTIONS: ApplicationSelectOption[] = [
  { value: "piercing-ngb", label: "Прокол / НГБ" },
  { value: "open-method", label: "Открытый способ" },
  { value: "overhead-crossing", label: "Воздушный переход" },
  { value: "other-method", label: "Иной способ" },
];

export const CROSSING_UTILITY_STRUCTURE_TYPE_OPTIONS: ApplicationSelectOption[] = [
  { value: "power-supply", label: "Электроснабжение" },
  { value: "water-supply", label: "Водоснабжение" },
  { value: "sewerage", label: "Водоотведение (канализация)" },
  { value: "heat-supply", label: "Теплоснабжение" },
  { value: "gas-supply", label: "Газоснабжение" },
  { value: "telecom", label: "Связь и телекоммуникации" },
  { value: "oil-pipeline", label: "Нефтепроводы и нефтепродуктопроводы" },
];

export const CROSSING_PLANNED_WORK_TYPE_OPTIONS: ApplicationSelectOption[] = [
  { value: "construction", label: "Строительство" },
  { value: "reconstruction", label: "Реконструкция" },
  { value: "major-repair", label: "Капитальный ремонт" },
];

export const SHIPMENT_TYPE_OPTIONS: ApplicationSelectOption[] = [
  { value: "group", label: "Групповая" },
  { value: "container", label: "Контейнерные перевозки" },
  { value: "route", label: "Маршрутная" },
  { value: "wagon", label: "Повагонная" },
  { value: "mixed-shipment", label: "Смешанная" },
];

export const MESSAGE_TYPE_OPTIONS: ApplicationSelectOption[] = [
  { value: "domestic", label: "Внутренние перевозки" },
  { value: "import", label: "Импорт" },
  { value: "export", label: "Экспорт" },
  { value: "transit", label: "Транзит" },
  { value: "mixed-message", label: "Смешанное" },
];

export const getSelectOptionLabel = (options: ApplicationSelectOption[], value: string | undefined): string => {
  if (!value?.trim()) {
    return "—";
  }

  return options.find((option) => option.value === value)?.label ?? value;
};
